/**
 * Items v2 smith actions (docs/systems/ITEMS_V2.md §2, R3–R4): ล้างค่า, ขัดเกลา, ผนึกค่า, ฝังหยก / ถอดหยก, เจาะรู, รวมหยก and
 * แยกชิ้น, on rolled items in the bag, at a smith only (like ตีบวก). Every action checks the item, the smith, the
 * ตำลึง and the materials before it takes anything; rolls go through the World's `Rng` only. Costs and materials are
 * data/itemCraft.ts.
 *
 * - ล้างค่า (`itemReroll`): one affix (`idx`) becomes another from the item's pool (group rules hold), or, without
 *   `idx`, every unlocked one does. The cost doubles per reroll already done on the item, and per lock.
 * - ขัดเกลา (`itemHone`): one affix keeps its kind and rolls a new quality.
 * - ผนึกค่า (`itemLock`): locks or unlocks an affix (at most `CRAFT_COST.maxLocks`); a locked affix is kept by ล้างค่า.
 * - ฝังหยก (`itemSocket`), ถอดหยก (`itemUnsocket`, the gem comes back), เจาะรู (`itemDrill`, up to the rarity's max).
 * - รวมหยก (`gemMerge`): three gems of a grade into one of the next.
 * - แยกชิ้น (`itemSalvage`): a piece not worn becomes materials by rarity; its gems come back.
 * Signature affixes (`imp`) are never rerolled, honed or locked.
 */
import { BOSS_TOKEN_TARGET } from '../data/bossGear';
import { MOUNTS } from '../data/mounts';
import { followPetPick, PETS } from '../data/pets';
import { AFFIXES, affixPool, affixValue, budgetShare, ILV_MAX, type AffixRoll } from '../data/affixes';
import { GOODS } from '../data/professions';
import {
  CRAFT_COST,
  CRAFT_MATS,
  CRAFT_SOURCES,
  GEM_KINDS,
  type CraftLoot,
  drillCost,
  GEM_MAX_GRADE,
  GEM_MERGE,
  gemId,
  gemOf,
  honeCost,
  MAX_SOCKETS,
  RAISE,
  raiseCost,
  rerollCost,
  SALVAGE,
} from '../data/itemCraft';
import { baseIlv, BAG_SIZE, ITEMS, rollItem, rollStats, type ItemRoll } from '../data/items';
import { WB_CHEST, WORLD_BOSSES } from '../data/worldBosses';
import { WORLD_EVENT_INFO } from '../data/worldEvents';
import { MARTIAL_ARTS } from '../data/skills';
import { SCHOOLS } from '../data/schools';
import { nearSmith } from './items';
import type { WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** Commands (part of `PlayerCommand`, through systems/items.ts `ItemsCommand`). */
export type ItemCraftCommand =
  | { t: 'itemReroll'; id: string; idx?: number }
  | { t: 'itemHone'; id: string; idx: number }
  | { t: 'itemLock'; id: string; idx: number }
  | { t: 'itemSocket'; id: string; gem: string }
  | { t: 'itemUnsocket'; id: string; idx: number }
  | { t: 'itemDrill'; id: string }
  | { t: 'gemMerge'; gem: string }
  | { t: 'itemSalvage'; id: string }
  | { t: 'itemRaise'; id: string }
  /** Turn a world-boss chest token in the bag into its pet or mount (data/bossGear.ts `BOSS_TOKEN_TARGET`). */
  | { t: 'redeemToken'; id: string };

/** Events (part of `WorldEvent`, through systems/items.ts `ItemsEvent`): a smith action done on `item`. */
export type ItemCraftEvent = {
  type: 'itemCraft';
  what: 'reroll' | 'hone' | 'lock' | 'socket' | 'unsocket' | 'drill' | 'merge' | 'salvage' | 'raise';
  item: string;
};

/** The boss whose chest pays this base, if any. */
const bossOf = (base: string) => WORLD_BOSSES.find((b) => b.rare.some((r) => r.item === base));

/** The highest item level a piece may be raised to (its source's cap). */
export function raiseCapOf(r: ItemRoll): number {
  const boss = bossOf(r.base);
  const base = ITEMS[r.base];
  return Math.min(ILV_MAX, boss ? boss.level + RAISE.overBoss : (base ? baseIlv(base) : 1) + RAISE.other);
}

/** The materials one หลอมยกระดับ of this piece takes. */
export function raiseMats(r: ItemRoll): Readonly<Record<string, number>> {
  const m = bossOf(r.base)?.materials[0];
  return m && GOODS[m] ? { [m]: RAISE.bossMats } : RAISE.otherMats;
}

type Cmd<T extends ItemCraftCommand['t']> = Extract<ItemCraftCommand, { t: T }>;

export class ItemCraftSystem implements WorldSystem {
  readonly name = 'itemCraft';

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    itemReroll: (c: Cmd<'itemReroll'>) => this.reroll(c.id, c.idx),
    itemHone: (c: Cmd<'itemHone'>) => this.hone(c.id, c.idx),
    itemLock: (c: Cmd<'itemLock'>) => this.lock(c.id, c.idx),
    itemSocket: (c: Cmd<'itemSocket'>) => this.socket(c.id, c.gem),
    itemUnsocket: (c: Cmd<'itemUnsocket'>) => this.unsocket(c.id, c.idx),
    itemDrill: (c: Cmd<'itemDrill'>) => this.drill(c.id),
    gemMerge: (c: Cmd<'gemMerge'>) => this.merge(c.gem),
    itemSalvage: (c: Cmd<'itemSalvage'>) => this.salvage(c.id),
    itemRaise: (c: Cmd<'itemRaise'>) => this.raise(c.id),
    redeemToken: (c: Cmd<'redeemToken'>) => this.redeem(c.id),
  };

  /** A token becomes its pet or mount and leaves the bag; one already owned keeps the token (nothing is lost). */
  private redeem(id: string): void {
    const pr = this.pr;
    const to = typeof id === 'string' ? BOSS_TOKEN_TARGET[id] : undefined;
    const at = to ? pr.inventory.indexOf(id) : -1;
    if (!to || at < 0) return this.h.notice('ไม่มีใบรับตัวนี้ในกระเป๋า');
    const owned = to.kind === 'pet' ? pr.pets : pr.mounts;
    const def = to.kind === 'pet' ? PETS[to.id] : MOUNTS[to.id];
    if (!def) return;
    if (owned.includes(to.id)) return this.h.notice(`มี${def.name}อยู่แล้ว เก็บใบรับตัวไว้แลกหรือขายต่อ`);
    pr.inventory.splice(at, 1);
    owned.push(to.id);
    if (to.kind === 'pet') {
      pr.petPick = to.id;
      followPetPick(this.h.world.pet, to.id);
    }
    else pr.mountPick = to.id;
    this.h.notice(`ได้${def.name}แล้ว`);
    this.h.log('sys', `แลกใบรับตัวเป็น${def.name}`);
    this.h.emit({ type: 'progressChanged' });
  }

  // ── sources: the big activities pay materials and gems; a world boss's gear comes rolled ──

  observe(ev: WorldEvent): void {
    if (ev.type === 'worldBossLoot' && ev.full && ev.tier !== 'none') {
      const boss = WORLD_BOSSES.find((b) => b.id === ev.id);
      // The chest's own materials (systems/worldBosses.ts pays only items; these are goods).
      if (boss?.materials.length) {
        const mats: Record<string, number> = {};
        for (let i = 0; i < WB_CHEST[ev.tier].materials; i++) {
          const m = boss.materials[i % boss.materials.length]!;
          if (GOODS[m]) mats[m] = (mats[m] ?? 0) + 1;
        }
        this.pay({ mats, gems: [] }, boss.name);
      }
      this.pay(CRAFT_SOURCES.worldBoss[ev.tier], 'หีบบอสโลก');
      const level = boss?.level ?? 30;
      for (const item of ev.items) this.rollGiven(item, level);
    } else if (ev.type === 'dungeonClear' && ev.rewarded) this.pay(CRAFT_SOURCES.dungeon, 'หีบดันเจี้ยน');
    else if (ev.type === 'worldEvent' && ev.state === 'end' && ev.win && (ev.id === 'raid' || ev.id === 'fair'))
      this.pay(CRAFT_SOURCES[ev.id], WORLD_EVENT_INFO[ev.id].name);
  }

  /** Materials and gems of a source into the goods (a gem's kind is one draw). */
  private pay(loot: CraftLoot, from: string): void {
    const got: string[] = [];
    for (const [g, n] of Object.entries(loot.mats)) {
      this.give(g, n);
      got.push(`${GOODS[g]?.name ?? g} ×${n}`);
    }
    for (const grade of loot.gems) {
      const kind = GEM_KINDS[Math.floor(this.h.random() * GEM_KINDS.length)]!;
      const g = gemId(kind.id, grade);
      this.give(g, 1);
      got.push(GOODS[g]?.name ?? g);
    }
    if (got.length) this.h.log('sys', `${from}: ได้ ${got.join(' · ')}`);
  }

  /** An equipment piece a chest just put in the bag becomes a rolled one at `level`. */
  private rollGiven(item: string, level: number): void {
    const pr = this.pr;
    const it = ITEMS[item];
    const i = pr.inventory.lastIndexOf(item);
    if (!it || i < 0 || pr.inventory.length > BAG_SIZE) return;
    const rid = `roll-${pr.nextRoll++}`;
    pr.rolls[rid] = rollItem(item, level, it.rarity, () => this.h.random(), {
      arts: Object.values(MARTIAL_ARTS).filter((a) => !a.hidden).map((a) => a.id),
      schools: Object.keys(SCHOOLS),
    });
    pr.inventory[i] = rid;
  }

  // ── shared checks ──

  private get pr() {
    return this.h.world.progress;
  }

  private atSmith(): boolean {
    if (nearSmith(this.h.world)) return true;
    this.h.notice('งานนี้ทำได้ที่ช่างตีเหล็กเท่านั้น');
    return false;
  }

  /** A rolled item of the bag with affixes, or null (with a notice). */
  private rolled(id: string): ItemRoll | null {
    const r = this.pr.rolls[id];
    if (typeof id !== 'string' || !r?.affixes || !this.pr.inventory.includes(id)) {
      this.h.notice('ต้องเป็นอุปกรณ์ที่มีค่าพลังสุ่มในกระเป๋า');
      return null;
    }
    return r;
  }

  private have(good: string): number {
    return this.pr.trades.goods[good] ?? 0;
  }

  private take(good: string, n: number): void {
    const left = this.have(good) - n;
    if (left > 0) this.pr.trades.goods[good] = left;
    else delete this.pr.trades.goods[good];
  }

  private give(good: string, n: number): void {
    this.pr.trades.goods[good] = this.have(good) + n;
  }

  /** Pays ตำลึง and materials when all are there; false (with a notice) when not. */
  private payCost(gold: number, mats: Readonly<Record<string, number>>): boolean {
    if (this.pr.gold < gold) {
      this.h.notice(`ตำลึงไม่พอ (ต้องการ ${gold})`);
      return false;
    }
    for (const [g, n] of Object.entries(mats))
      if (n > 0 && this.have(g) < n) {
        this.h.notice(`${GOODS[g]?.name ?? g}ไม่พอ (ต้องการ ${n})`);
        return false;
      }
    this.pr.gold -= gold;
    for (const [g, n] of Object.entries(mats)) if (n > 0) this.take(g, n);
    return true;
  }

  private save(id: string, r: ItemRoll, what: ItemCraftEvent['what'], text: string): void {
    const rs = rollStats(r);
    const art = Object.keys(rs.arts)[0];
    const { art: _old, ...rest } = r;
    void _old;
    this.pr.rolls[id] = { ...rest, stats: rs.stats, ...(art ? { art: { id: art, pct: rs.arts[art]! } } : {}) };
    this.h.emit({ type: 'itemCraft', what, item: id });
    if (Object.values(this.pr.equipment).includes(id)) this.h.world.refreshPlayerStats();
    this.h.emit({ type: 'progressChanged' });
    this.h.log('sys', text);
  }

  private ilvOf(r: ItemRoll): number {
    return r.ilv ?? baseIlv(ITEMS[r.base]!);
  }

  // ── ล้างค่า / ขัดเกลา / ผนึกค่า ──

  private reroll(id: string, idx?: number): void {
    if (!this.atSmith()) return;
    const r = this.rolled(id);
    if (!r) return;
    const affixes = [...r.affixes!];
    const targets =
      idx === undefined
        ? affixes.map((a, i) => (a.imp || a.locked ? -1 : i)).filter((i) => i >= 0)
        : Number.isInteger(idx) && affixes[idx] && !affixes[idx]!.imp && !affixes[idx]!.locked
          ? [idx]
          : [];
    if (!targets.length) return this.h.notice('ไม่มีค่าพลังให้ล้าง (ค่าที่ผนึกหรือค่าประจำตัวล้างไม่ได้)');
    const locks = affixes.filter((a) => a.locked).length;
    const ilv = this.ilvOf(r);
    const cost = rerollCost(ilv, r.rerolls ?? 0, locks);
    if (!this.payCost(cost, { [CRAFT_MATS.reroll]: 1, [CRAFT_MATS.seal]: locks })) return;
    const base = ITEMS[r.base]!;
    const rarity = r.rarity ?? base.rarity;
    const share = budgetShare(rarity, affixes.filter((a) => !a.imp).length);
    const pool = affixPool(base, { arts: Object.values(MARTIAL_ARTS).filter((a) => !a.hidden).map((a) => a.id), schools: Object.keys(SCHOOLS) });
    for (const i of targets) {
      // Any affix of the pool whose group no other affix holds (the one rerolled may come back).
      const taken = new Set(affixes.filter((_, j) => j !== i).map((a) => AFFIXES[a.id]?.group ?? a.id));
      const free = pool.filter((a) => !taken.has(a.group ?? a.id));
      if (!free.length) continue;
      const a = free[Math.floor(this.h.random() * free.length)]!;
      const args = a.id === 'artDmg' ? Object.values(MARTIAL_ARTS).filter((m) => !m.hidden).map((m) => m.id) : a.id === 'schoolDmg' ? Object.keys(SCHOOLS) : null;
      const arg = args ? args[Math.floor(this.h.random() * args.length)] : undefined;
      const q = Math.floor(this.h.random() * 101);
      affixes[i] = { id: a.id, v: affixValue(a, ilv, q, share), q, ...(arg ? { arg } : {}) };
    }
    this.save(id, { ...r, affixes, rerolls: (r.rerolls ?? 0) + 1 }, 'reroll', `ล้างค่าสำเร็จ (${cost} ตำลึง)`);
  }

  private hone(id: string, idx: number): void {
    if (!this.atSmith()) return;
    const r = this.rolled(id);
    if (!r) return;
    const a = r.affixes![idx];
    const def = a && AFFIXES[a.id];
    if (!Number.isInteger(idx) || !a || !def || a.imp) return this.h.notice('เลือกค่าพลังที่จะขัดเกลา');
    const ilv = this.ilvOf(r);
    const cost = honeCost(ilv);
    if (!this.payCost(cost, { [CRAFT_MATS.hone]: 1 })) return;
    const base = ITEMS[r.base]!;
    const share = budgetShare(r.rarity ?? base.rarity, r.affixes!.filter((x) => !x.imp).length);
    const q = Math.floor(this.h.random() * 101);
    const affixes: AffixRoll[] = r.affixes!.map((x, i) => (i === idx ? { ...x, q, v: affixValue(def, ilv, q, share) } : x));
    this.save(id, { ...r, affixes }, 'hone', `ขัดเกลาค่าพลัง คุณภาพ ${a.q}% → ${q}%`);
  }

  private lock(id: string, idx: number): void {
    const r = this.rolled(id);
    if (!r) return;
    const a = r.affixes![idx];
    if (!Number.isInteger(idx) || !a || a.imp) return this.h.notice('เลือกค่าพลังที่จะผนึก');
    if (!a.locked && r.affixes!.filter((x) => x.locked).length >= CRAFT_COST.maxLocks)
      return this.h.notice(`ผนึกค่าได้ไม่เกิน ${CRAFT_COST.maxLocks} บรรทัด`);
    const affixes = r.affixes!.map((x, i) => {
      if (i !== idx) return x;
      const { locked: _l, ...rest } = x;
      void _l;
      return a.locked ? rest : { ...rest, locked: true };
    });
    this.save(id, { ...r, affixes }, 'lock', a.locked ? 'ปลดผนึกค่าพลัง' : 'ผนึกค่าพลังไว้ ล้างค่าจะไม่เปลี่ยนบรรทัดนี้');
  }

  // ── ฝังหยก / ถอดหยก / เจาะรู / รวมหยก ──

  private socket(id: string, gem: string): void {
    if (!this.atSmith()) return;
    const r = this.rolled(id);
    if (!r) return;
    if (!gemOf(gem) || this.have(gem) < 1) return this.h.notice('ไม่มีหยกนี้');
    const sockets = [...(r.sockets ?? [])];
    const i = sockets.indexOf(null);
    if (i < 0) return this.h.notice('ไม่มีรูว่างให้ฝังหยก');
    this.take(gem, 1);
    sockets[i] = gem;
    this.save(id, { ...r, sockets }, 'socket', `ฝัง「${GOODS[gem]?.name ?? gem}」สำเร็จ`);
  }

  private unsocket(id: string, idx: number): void {
    if (!this.atSmith()) return;
    const r = this.rolled(id);
    if (!r) return;
    const gem = r.sockets?.[idx];
    const g = gem ? gemOf(gem) : null;
    if (!Number.isInteger(idx) || !gem || !g) return this.h.notice('รูนี้ไม่มีหยก');
    if (!this.payCost(CRAFT_COST.unsocketPerGrade * g.grade, {})) return;
    this.give(gem, 1);
    const sockets = r.sockets!.map((x, i) => (i === idx ? null : x));
    this.save(id, { ...r, sockets }, 'unsocket', `ถอด「${GOODS[gem]?.name ?? gem}」คืนกระเป๋า`);
  }

  private drill(id: string): void {
    if (!this.atSmith()) return;
    const r = this.rolled(id);
    if (!r) return;
    const max = MAX_SOCKETS[r.rarity ?? ITEMS[r.base]!.rarity];
    const n = r.sockets?.length ?? 0;
    if (n >= max) return this.h.notice(max ? 'เจาะรูครบแล้ว' : 'ของระดับนี้เจาะรูไม่ได้');
    if (!this.payCost(drillCost(this.ilvOf(r)), { [CRAFT_MATS.drill]: 1 })) return;
    this.save(id, { ...r, sockets: [...(r.sockets ?? []), null] }, 'drill', `เจาะรูสำเร็จ (${n + 1}/${max})`);
  }

  private merge(gem: string): void {
    if (!this.atSmith()) return;
    const g = gemOf(gem);
    if (!g) return this.h.notice('ไม่มีหยกนี้');
    if (g.grade >= GEM_MAX_GRADE) return this.h.notice('หยกขั้นสูงสุดแล้ว');
    if (!this.payCost(CRAFT_COST.mergePerGrade * g.grade, { [gem]: GEM_MERGE })) return;
    const next = gemId(g.kind.id, g.grade + 1);
    this.give(next, 1);
    this.h.emit({ type: 'itemCraft', what: 'merge', item: next });
    this.h.emit({ type: 'progressChanged' });
    this.h.log('sys', `รวมหยกได้「${GOODS[next]?.name ?? next}」`);
  }

  // ── หลอมยกระดับ ──


  private raise(id: string): void {
    if (!this.atSmith()) return;
    const r = this.rolled(id);
    if (!r) return;
    const ilv = this.ilvOf(r);
    const cap = raiseCapOf(r);
    if (ilv >= cap) return this.h.notice(`ระดับไอเทมถึงขีดสุดของแหล่งที่มาแล้ว (${cap})`);
    const mats = raiseMats(r);
    if (!this.payCost(raiseCost(ilv), mats)) return;
    const next = Math.min(cap, ilv + RAISE.step);
    const base = ITEMS[r.base]!;
    const share = budgetShare(r.rarity ?? base.rarity, r.affixes!.filter((a) => !a.imp).length);
    // Qualities kept; values follow the new band.
    const affixes = r.affixes!.map((a) => {
      const d = AFFIXES[a.id];
      return a.imp || !d ? a : { ...a, v: affixValue(d, next, a.q, share) };
    });
    this.save(id, { ...r, ilv: next, affixes }, 'raise', `หลอมยกระดับ ระดับไอเทม ${ilv} → ${next}`);
  }

  // ── แยกชิ้น ──

  private salvage(id: string): void {
    const pr = this.pr;
    if (!this.atSmith()) return;
    const r = this.rolled(id);
    if (!r) return;
    if (Object.values(pr.equipment).includes(id)) return this.h.notice('ถอดอุปกรณ์ออกก่อนแยกชิ้น');
    const got = SALVAGE[r.rarity ?? ITEMS[r.base]!.rarity];
    pr.inventory.splice(pr.inventory.indexOf(id), 1);
    delete pr.rolls[id];
    for (const [g, n] of Object.entries(got)) this.give(g, n);
    for (const gem of r.sockets ?? []) if (gem) this.give(gem, 1);
    this.h.emit({ type: 'itemCraft', what: 'salvage', item: id });
    this.h.emit({ type: 'progressChanged' });
    this.h.log(
      'sys',
      `แยกชิ้น「${ITEMS[r.base]!.name}」ได้ ${Object.entries(got)
        .map(([g, n]) => `${GOODS[g]?.name ?? g} ×${n}`)
        .join(' ')}`,
    );
  }
}
